import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  Chip,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip,
} from '@mui/material';
import { motion } from 'framer-motion';
import {
  Search,
  ExpandMore,
  OpenInNew,
  School,
  TrendingUp,
  CalendarToday,
  Person,
} from '@mui/icons-material';
import profileConfig from '../constants/profileConfig';

const Publications = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedYear, setSelectedYear] = useState('all');

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
      },
    },
  };

  // Get unique years for filter
  const years = [...new Set(profileConfig.publications.map(pub => pub.year))].sort((a, b) => b - a);

  // Filter publications based on search term and year
  const filteredPublications = profileConfig.publications.filter(pub => {
    const matchesSearch = 
      pub.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      pub.authors.toLowerCase().includes(searchTerm.toLowerCase()) ||
      pub.journal.toLowerCase().includes(searchTerm.toLowerCase()) ||
      pub.abstract.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesYear = selectedYear === 'all' || pub.year === parseInt(selectedYear);
    
    return matchesSearch && matchesYear;
  });

  const totalCitations = profileConfig.publications.reduce((sum, pub) => sum + pub.citations, 0);

  return (
    <Box sx={{ py: 4 }}>
      <Container maxWidth="lg">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Header Section */}
          <motion.div variants={itemVariants}>
            <Typography
              variant="h2"
              align="center"
              sx={{ mb: 2, fontWeight: 600 }}
            >
              Publications
            </Typography>
            <Typography
              variant="h6"
              align="center"
              color="text.secondary"
              sx={{ mb: 6 }}
            >
              Published research papers and academic contributions
            </Typography>
          </motion.div>

          {/* Statistics */}
          <motion.div variants={itemVariants}>
            <Grid container spacing={3} sx={{ mb: 6 }}>
              <Grid item xs={12} sm={4}>
                <Card sx={{ textAlign: 'center', p: 3 }}>
                  <Typography variant="h3" sx={{ fontWeight: 700, color: 'primary.main' }}>
                    {profileConfig.publications.length}
                  </Typography>
                  <Typography variant="body1">Total Publications</Typography>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Card sx={{ textAlign: 'center', p: 3 }}>
                  <Typography variant="h3" sx={{ fontWeight: 700, color: 'primary.main' }}>
                    {totalCitations}
                  </Typography>
                  <Typography variant="body1">Total Citations</Typography>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Card sx={{ textAlign: 'center', p: 3 }}>
                  <Typography variant="h3" sx={{ fontWeight: 700, color: 'primary.main' }}>
                    {Math.round(totalCitations / profileConfig.publications.length)}
                  </Typography>
                  <Typography variant="body1">Average Citations</Typography>
                </Card>
              </Grid>
            </Grid>
          </motion.div>

          {/* Search and Filter */}
          <motion.div variants={itemVariants}>
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  placeholder="Search publications by title, authors, journal, or abstract..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  select
                  label="Filter by Year"
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                  SelectProps={{
                    native: true,
                  }}
                >
                  <option value="all">All Years</option>
                  {years.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </TextField>
              </Grid>
            </Grid>
          </motion.div>

          {/* Publications List */}
          <motion.div variants={itemVariants}>
            {filteredPublications.length === 0 ? (
              <Card sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="h6" color="text.secondary">
                  No publications found matching your search criteria.
                </Typography>
              </Card>
            ) : (
              <Grid container spacing={3}>
                {filteredPublications.map((publication, index) => (
                  <Grid item xs={12} key={publication.id}>
                    <motion.div
                      variants={itemVariants}
                      whileHover={{ scale: 1.02 }}
                    >
                      <Card>
                        <CardContent sx={{ p: 3 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, flex: 1 }}>
                              {publication.title}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, ml: 2 }}>
                              <Chip
                                label={publication.year}
                                size="small"
                                icon={<CalendarToday />}
                                color="primary"
                              />
                              <Chip
                                label={`${publication.citations} citations`}
                                size="small"
                                icon={<TrendingUp />}
                                variant="outlined"
                              />
                            </Box>
                          </Box>

                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            <strong>Authors:</strong> {publication.authors}
                          </Typography>

                          <Typography variant="body2" color="primary" sx={{ mb: 2 }}>
                            <strong>Journal:</strong> {publication.journal}
                          </Typography>

                          <Typography variant="body2" sx={{ mb: 2, lineHeight: 1.6 }}>
                            <strong>Abstract:</strong> {publication.abstract}
                          </Typography>

                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body2" color="text.secondary">
                              <strong>DOI:</strong> {publication.doi}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Tooltip title="View Publication">
                                <IconButton
                                  component="a"
                                  href={publication.link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  size="small"
                                  color="primary"
                                >
                                  <OpenInNew />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    </motion.div>
                  </Grid>
                ))}
              </Grid>
            )}
          </motion.div>

          {/* Publication Categories */}
          <motion.div variants={itemVariants}>
            <Typography variant="h4" sx={{ mt: 6, mb: 4, fontWeight: 600 }}>
              Publication Categories
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Journal Publications
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                      Peer-reviewed articles published in high-impact journals including:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      <Chip label="Nature Machine Intelligence" size="small" />
                      <Chip label="IEEE Transactions" size="small" />
                      <Chip label="JMIR" size="small" />
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </Grid>

              <Grid item xs={12} md={6}>
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Conference Papers
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                      Papers presented at major international conferences including:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      <Chip label="NeurIPS" size="small" />
                      <Chip label="ICML" size="small" />
                      <Chip label="CVPR" size="small" />
                      <Chip label="AAAI" size="small" />
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </Grid>
            </Grid>
          </motion.div>

          {/* Call to Action */}
          <motion.div variants={itemVariants}>
            <Box
              sx={{
                mt: 6,
                p: 4,
                backgroundColor: 'primary.main',
                color: 'white',
                borderRadius: 2,
                textAlign: 'center',
              }}
            >
              <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
                Interested in Citing My Work?
              </Typography>
              <Typography variant="body1" sx={{ mb: 3 }}>
                If you find my research useful for your work, I'd be happy to discuss potential 
                collaborations or provide additional information about any of my publications.
              </Typography>
              <Button
                variant="contained"
                size="large"
                sx={{
                  backgroundColor: 'white',
                  color: 'primary.main',
                  '&:hover': {
                    backgroundColor: 'grey.100',
                  },
                }}
              >
                Contact for Collaboration
              </Button>
            </Box>
          </motion.div>
        </motion.div>
      </Container>
    </Box>
  );
};

export default Publications; 